import { replaceParams } from 'utils/helpers';
import api from './api';
import { apiService } from './services';
import { formatDate } from 'utils/date';
import { generatePresignedUrl } from 'components/pages/container-category/container-category.fetcher';
import { GENERATE_PRESIGNED_URL_TYPE } from 'utils/enums';
import axios, { HttpStatusCode } from 'axios';
import { hideLoading, showLoading } from 'reducer/rootReducer';
import { store } from 'store';
import { BASE_URL } from './axiosInstance';

export const fetchCompanyOptions = async (
    request: FilterRequest<string>,
): Promise<ApiResponse<ResponseBase<Company[]>>> => {
    return apiService.get<GetCompaniesRequest, ResponseBase<Company[]>>(
        api.companyOptions,
        request,
    );
};

export const fetchMajorLocationOptions = async (
    request: IMajorLocationOptionsRequest<string>,
): Promise<ApiResponse<ResponseBase<IMajorLocation[]>>> => {
    return apiService.get<FilterRequest<String>, ResponseBase<IMajorLocation[]>>(
        api.majorLocationOptions,
        request,
    );
};

export const fetchMiddleLocationOptions = async (
    request: IMiddleLocationOptionsRequest<string>,
): Promise<ApiResponse<ResponseBase<IMiddleLocation[]>>> => {
    if (!request.major_location_id) {
        return {
            success: false,
            error: '',
        };
    }

    return apiService.get<FilterRequest<String>, ResponseBase<IMiddleLocation[]>>(
        replaceParams(api.middleLocationOptions, {
            major_location_id: request.major_location_id,
        }),
        request,
    );
};

export const fetchMinorLocationOptions = async (
    request: IMinorLocationOptionsRequest<string>,
): Promise<ApiResponse<ResponseBase<IMinorLocation[]>>> => {
    if (!request.major_location_id || !request.middle_location_id) {
        return {
            success: false,
            error: '',
        };
    }

    return apiService.get<FilterRequest<String>, ResponseBase<IMinorLocation[]>>(
        replaceParams(api.minorLocationOptions, {
            major_location_id: request.major_location_id,
            middle_location_id: request.middle_location_id,
        }),
        request,
    );
};

export const fetchMajorContentOptions = async (
    request: IMajorContentOptionsRequest<string>,
): Promise<ApiResponse<ResponseBase<IMajorContent[]>>> => {
    return apiService.get<FilterRequest<String>, ResponseBase<IMajorContent[]>>(
        api.majorContentOptions,
        request,
    );
};

export const fetchMiddleContentOptions = async (
    request: IMiddleContentOptionsRequest<string>,
): Promise<ApiResponse<ResponseBase<IMiddleContent[]>>> => {
    if (!request.major_content_id) {
        return {
            success: false,
            error: '',
        };
    }

    return apiService.get<FilterRequest<String>, ResponseBase<IMiddleContent[]>>(
        replaceParams(api.middleContentOptions, { major_content_id: request.major_content_id }),
        request,
    );
};

export const fetchMinorContentOptions = async (
    request: IMinorContentOptionsRequest<string>,
): Promise<ApiResponse<ResponseBase<IMinorContent[]>>> => {
    if (!request.major_content_id || !request.middle_content_id) {
        return {
            success: false,
            error: '',
        };
    }

    return apiService.get<FilterRequest<String>, ResponseBase<IMinorContent[]>>(
        replaceParams(api.minorContentOptions, {
            major_content_id: request.major_content_id,
            middle_content_id: request.middle_content_id,
        }),
        request,
    );
};

export const fetchContainer = (request: IContainerDetailsRequest<string>) => {
    return apiService.get<FilterRequest<String>, ResponseBase<IContainer>>(
        replaceParams(api.containerDetails, {
            item_id: request.container_id,
        }),
        request,
        undefined,
        true,
    );
};
export const fetchUploadImage = async (
    data: (string | File)[],
    isShowLoading: boolean = true,
): Promise<string[]> => {
    if (!isShowLoading) {
        store.dispatch(showLoading());
    }
    const urls = await Promise.all(
        data.map(async (element) => {
            try {
                if (!element) return '';

                if (typeof element === 'string') return element;

                const path = `${formatDate(new Date(), 'YYYYMMDDHHmmss')}${element.name}`.trim();

                const uploadUrlRes = await generatePresignedUrl(
                    {
                        file_path: path,
                        operation: GENERATE_PRESIGNED_URL_TYPE.UPLOAD,
                    },
                    isShowLoading,
                );

                if (!uploadUrlRes.success) return '';

                const { presignedUrl } = uploadUrlRes.data.data;

                const uploadRes = await axios.put(presignedUrl, element, {
                    headers: {
                        'Content-Type': element.type,
                    },
                });

                if (uploadRes.status !== HttpStatusCode.Ok) return '';

                return path;
            } catch (err) {
                return '';
            }
        }),
    );
    if (!isShowLoading) {
        store.dispatch(hideLoading());
    }

    return urls;
};

export const getTaskCategoryOptionsFetcher = async (
    params: URLSearchParams,
): Promise<ApiResponse<IGetTaskCategoryRes>> => {
    return apiService.get<URLSearchParams, IGetTaskCategoryRes>(
        `${BASE_URL}/task-category/options`,
        params,
    );
};

export const getTaskContentOptionsFetcher = async (
    task_category_id: string,
    params?: URLSearchParams,
): Promise<ApiResponse<IGetTaskContentRes>> => {
    return apiService.get<URLSearchParams, IGetTaskContentRes>(
        `${BASE_URL}/task-content/${task_category_id}/options`,
        params,
    );
};
